import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { KEY_FIGURES } from "@/lib/marketPageData";

// Facts are pulled directly from the sourced figures already used on /market
// (Part 2 of the spec) — nothing invented here, just cycled for a "live
// data" feel. `region` drives which map pin lights up while a fact is shown
// ("all" pulses every pin, for combined/cross-region figures). Values that
// also appear on /market are pulled from the shared KEY_FIGURES constant so
// a correction there doesn't drift out of sync with the map.
const FACTS = [
  { v: KEY_FIGURES.aadForgetPct, t: "of people often forget to reapply sunscreen (AAD survey)", c: "#ff2d78", region: "united-states" },
  { v: KEY_FIGURES.aadReapplyShort, t: "reapply every 2 hours, the frequency the FDA advises", c: "#00e5ff", region: "united-states" },
  { v: KEY_FIGURES.euRoutineUsePct, t: "of Europeans use sunscreen routinely or often", c: "#e6c068", region: "europe" },
  { v: KEY_FIGURES.combinedPop2544Short, t: "people ages 25–44 across the US, Europe & Caribbean", c: "#3ad6a0", region: "all" },
  { v: KEY_FIGURES.coreAudienceShort, t: "estimated core, need-based potential consumers", c: "#00e5ff", region: "all" },
  { v: KEY_FIGURES.usPop2544, t: "adults ages 25–44 in the United States alone", c: "#ff2d78", region: "united-states" },
  { v: KEY_FIGURES.euPop2544, t: "adults ages 25–44 across Europe (EU-27 + UK)", c: "#b794f6", region: "europe" },
  { v: KEY_FIGURES.caribbeanArrivals, t: "Caribbean stay-over visitor arrivals in 2025", c: "#e6c068", region: "caribbean" },
];

// Priority/first markets are condensed straight from the /market region
// snapshots (Part 2.6) — the map is a compressed preview of that section,
// not decorative filler. `key` matches the `region` field on FACTS above so
// the currently-shown stat can light up its matching pin.
const PINS = [
  { key: "united-states", label: "United States", sub: "FL · CA · TX · HI", x: 17, y: 35, c: "#00e5ff", labelBelow: false },
  { key: "europe", label: "Europe", sub: "UK · Germany · France +4", x: 50, y: 27, c: "#ff2d78", labelBelow: false },
  { key: "caribbean", label: "Caribbean", sub: "Resort & marina markets", x: 24, y: 46, c: "#e6c068", labelBelow: true },
];

function Pin({ x, y, c, label, sub, delay, labelBelow, active }) {
  return (
    <span
      className="absolute"
      style={{ left: `${x}%`, top: `${y}%` }}
      data-testid={`market-pulse-pin-${label.toLowerCase().replace(/\s+/g, "-")}`}
      data-active={active}
    >
      <span
        className="absolute rounded-full border transition-all duration-500"
        style={{
          width: active ? 40 : 30, height: active ? 40 : 30, left: active ? -20 : -15, top: active ? -20 : -15,
          borderColor: c, boxShadow: `0 0 ${active ? 16 : 10}px ${c}${active ? "aa" : "66"}`,
          opacity: active ? 1 : 0.45,
          animation: `senseWave ${active ? 1.6 : 2.4}s ease-out ${delay}s infinite`,
        }}
      />
      <span
        className="absolute rounded-full transition-all duration-500"
        style={{
          left: active ? -5 : -4, top: active ? -5 : -4, width: active ? 10 : 8, height: active ? 10 : 8, background: c,
          boxShadow: `0 0 ${active ? 14 : 8}px ${c}, 0 0 ${active ? 26 : 16}px ${c}99`,
          opacity: active ? 1 : 0.55,
          animation: `senseCore 1.9s ease-in-out ${delay}s infinite`,
        }}
      />
      <span
        className="absolute w-px transition-opacity duration-500"
        style={{
          left: 0, height: 14,
          top: labelBelow ? 4 : "auto",
          bottom: labelBelow ? "auto" : 4,
          background: c, opacity: active ? 0.9 : 0.35,
        }}
      />
      <span
        className="absolute flex flex-col leading-none transition-opacity duration-500"
        data-testid={`market-pulse-label-${label.toLowerCase().replace(/\s+/g, "-")}`}
        style={{
          left: 8,
          top: labelBelow ? 18 : "auto",
          bottom: labelBelow ? "auto" : 18,
          animation: `calloutIn 0.5s ease-out ${delay + 0.3}s both`,
          opacity: active ? 1 : 0.8,
        }}
      >
        <span
          className="font-mono text-[9px] sm:text-[10.5px] tracking-[0.06em] uppercase whitespace-nowrap font-semibold transition-colors duration-500"
          style={{ color: active ? c : "#ffffff", textShadow: "0 1px 8px rgba(0,0,0,0.95)" }}
        >
          {label}
        </span>
        <span
          className="font-mono text-[7.5px] sm:text-[8.5px] tracking-[0.03em] text-white/75 whitespace-nowrap mt-0.5"
          style={{ textShadow: "0 1px 8px rgba(0,0,0,0.95)" }}
        >
          {sub}
        </span>
      </span>
    </span>
  );
}

/**
 * Illustrated dark-mode world map with pulsing, labeled region pins (US /
 * Europe / Caribbean — each annotated with its actual priority/first
 * markets from the /market region snapshots) plus a digital-readout style
 * ticker cycling through sourced /market facts. A condensed, "alive"
 * preview of the /market page itself — used on the homepage teaser and the
 * /market hero.
 */
export function MarketPulseMap({ className = "" }) {
  const [i, setI] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setI((n) => (n + 1) % FACTS.length), 3500);
    return () => clearInterval(id);
  }, []);

  const fact = FACTS[i];
  const activeKey = fact.region;

  return (
    <div
      className={`relative overflow-hidden border border-zinc-800 bg-black aspect-[1264/848] ${className}`}
      data-testid="market-pulse-map"
    >
      <img
        src="https://static.prod-images.emergentagent.com/jobs/ec62b2d0-2ad9-4890-99e3-2e35ee4d6bac/images/cab725463b24ddca6620cac26e0b00e212c9d16610c7229c66fba45645572d35.jpeg"
        alt="Colorful glowing world map highlighting WearSpray's priority United States, Europe and Caribbean markets"
        className="absolute inset-0 w-full h-full object-cover opacity-90"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/10 to-black/20" />

      {/* ambient radar-style scan sweep */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        <div
          className="absolute left-0 right-0 h-20"
          style={{
            background: "linear-gradient(to bottom, transparent, rgba(0,229,255,0.10), transparent)",
            animation: "hudScan 6s linear infinite",
          }}
        />
      </div>

      <div className="absolute inset-0">
        {PINS.map(({ key, ...p }, idx) => (
          <Pin key={key} {...p} delay={idx * 0.3} active={activeKey === "all" || activeKey === key} />
        ))}
      </div>

      {/* live badge */}
      <div className="absolute top-3.5 right-4 flex items-center gap-2" data-testid="market-pulse-live-badge">
        <span className="relative flex h-2 w-2">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#3ad6a0] opacity-75" style={{ animation: "senseCore 1.6s ease-in-out infinite" }} />
          <span className="relative inline-flex rounded-full h-2 w-2 bg-[#3ad6a0]" />
        </span>
        <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#3ad6a0]">Live</span>
      </div>

      {/* digital-readout stat panel */}
      <div className="absolute bottom-0 left-0 right-0 px-4 sm:px-5 py-3 border-t border-white/10 bg-black/65 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-1.5">
          <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc-500 flex items-center gap-1">
            Market Scan
            <span style={{ animation: "blinkCursor 1s step-end infinite" }}>_</span>
          </span>
          <span className="font-mono text-[9px] tracking-[0.15em] text-zinc-600 flex items-center gap-2" data-testid="market-pulse-region-indicator">
            <motion.span
              key={activeKey}
              initial={{ opacity: 0.4 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.25 }}
              style={{ color: fact.c }}
              className="uppercase"
            >
              {activeKey === "all" ? "Global" : activeKey.replace("-", " ")}
            </motion.span>
            <span>{String(i + 1).padStart(2, "0")}/{String(FACTS.length).padStart(2, "0")}</span>
          </span>
        </div>
        <AnimatePresence mode="wait">
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="flex items-baseline gap-2.5"
            data-testid="market-pulse-fact"
          >
            <span className="font-heading text-xl sm:text-2xl font-light tracking-tight" style={{ color: fact.c }}>
              {fact.v}
            </span>
            <span className="font-body text-xs sm:text-sm text-zinc-300 leading-snug">{fact.t}</span>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
